import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const OverrideModal = ({ lawyers, isAdminOrPartner, currentUserId, currentRate, onSave, onClose }) => {
  const [lawyerId, setLawyerId] = useState(currentUserId || '');
  const [caseId, setCaseId] = useState('');
  const [caseName, setCaseName] = useState('');
  const [standardRate, setStandardRate] = useState(currentRate || '');
  const [overrideRate, setOverrideRate] = useState('');
  const [justification, setJustification] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!caseName?.trim()) newErrors.caseName = 'Case name is required';
    if (!standardRate || Number(standardRate) <= 0) newErrors.standardRate = 'Standard rate required';
    if (!overrideRate || Number(overrideRate) <= 0) newErrors.overrideRate = 'Override rate required';
    if (!justification?.trim()) newErrors.justification = 'Justification is required';
    if (!effectiveDate) newErrors.effectiveDate = 'Effective date required';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!validate()) return;
    onSave({
      lawyerId: isAdminOrPartner ? lawyerId : currentUserId,
      caseId,
      caseName,
      standardRate: Number(standardRate),
      overrideRate: Number(overrideRate),
      justification,
      effectiveDate
    });
  };

  const rateChange = overrideRate && standardRate
    ? (((Number(overrideRate) - Number(standardRate)) / Number(standardRate)) * 100)?.toFixed(1)
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-2xl border border-border w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-surface">
          <h2 className="font-heading-semibold text-text-primary">Request Case Rate Override</h2>
          <button onClick={onClose} className="p-2 hover:bg-secondary-50 rounded-lg transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {isAdminOrPartner && lawyers?.length > 0 && (
            <div>
              <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Lawyer</label>
              <select
                value={lawyerId}
                onChange={e => setLawyerId(e?.target?.value)}
                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface"
              >
                <option value="">Select lawyer...</option>
                {lawyers?.map(l => (
                  <option key={l?.id} value={l?.id}>{l?.full_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Case Name <span className="text-error-500">*</span></label>
              <input
                type="text"
                value={caseName}
                onChange={e => setCaseName(e?.target?.value)}
                placeholder="Enter case name"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors?.caseName ? 'border-error-300' : 'border-border'
                }`}
              />
              {errors?.caseName && <p className="text-xs text-error-500 mt-1">{errors?.caseName}</p>}
            </div>
            <div>
              <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Case ID</label>
              <input
                type="text"
                value={caseId}
                onChange={e => setCaseId(e?.target?.value)}
                placeholder="e.g. CASE-2024-001"
                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Standard Rate (KES) <span className="text-error-500">*</span></label>
              <input
                type="number"
                value={standardRate}
                onChange={e => setStandardRate(e?.target?.value)}
                min="0"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors?.standardRate ? 'border-error-300' : 'border-border'
                }`}
              />
              {errors?.standardRate && <p className="text-xs text-error-500 mt-1">{errors?.standardRate}</p>}
            </div>
            <div>
              <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Override Rate (KES) <span className="text-error-500">*</span></label>
              <input
                type="number"
                value={overrideRate}
                onChange={e => setOverrideRate(e?.target?.value)}
                min="0"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors?.overrideRate ? 'border-error-300' : 'border-border'
                }`}
              />
              {errors?.overrideRate && <p className="text-xs text-error-500 mt-1">{errors?.overrideRate}</p>}
            </div>
          </div>
          {rateChange !== null && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
              Number(rateChange) > 0 ? 'bg-success-50 text-success-700' : Number(rateChange) < 0 ? 'bg-error-50 text-error-700' : 'bg-secondary-50 text-text-secondary'
            }`}>
              <Icon name={Number(rateChange) > 0 ? 'TrendingUp' : Number(rateChange) < 0 ? 'TrendingDown' : 'Minus'} size={14} />
              Rate change: {Number(rateChange) > 0 ? '+' : ''}{rateChange}%
            </div>
          )}
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Effective Date <span className="text-error-500">*</span></label>
            <input
              type="date"
              value={effectiveDate}
              onChange={e => setEffectiveDate(e?.target?.value)}
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary ${
                errors?.effectiveDate ? 'border-error-300' : 'border-border'
              }`}
            />
            {errors?.effectiveDate && <p className="text-xs text-error-500 mt-1">{errors?.effectiveDate}</p>}
          </div>
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-1.5">Justification <span className="text-error-500">*</span></label>
            <textarea
              value={justification}
              onChange={e => setJustification(e?.target?.value)}
              placeholder="Explain the reason for this rate override..."
              rows={3}
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none ${
                errors?.justification ? 'border-error-300' : 'border-border'
              }`}
            />
            {errors?.justification && <p className="text-xs text-error-500 mt-1">{errors?.justification}</p>}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 bg-primary text-white py-2.5 rounded-lg font-body-medium hover:bg-primary-700 transition-colors"
            >
              Submit Override Request
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border py-2.5 rounded-lg text-sm font-body-medium text-text-secondary hover:bg-secondary-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OverrideModal;
